import React from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  GridItem,
  Heading,
  Icon,
  Image,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  useColorModeValue,
  SimpleGrid,
  Flex,
  Badge
} from '@chakra-ui/react';
import {
  FaChalkboardTeacher,
  FaUserGraduate,
  FaBook,
  FaLightbulb,
  FaBullseye,
  FaChartLine,
  FaUsers,
  FaCheckCircle
} from 'react-icons/fa';
import { MdSchool } from 'react-icons/md';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const AboutContent = () => {
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  const values = [
    {
      title: 'التميز الأكاديمي',
      description: 'نقدم محتوى تعليمي دقيق ومبسط يتوافق مع المعايير التربوية التونسية لضمان أفضل النتائج.',
      icon: FaBook,
      color: primaryColor
    },
    {
      title: 'مرافقة التلاميذ',
      description: 'نحن نؤمن بأن كل تلميذ يحتاج إلى اهتمام خاص، لذا نوفر متابعة مستمرة وتواصلاً مباشراً مع الأساتذة.',
      icon: FaUserGraduate,
      color: '#D69E2E'
    },
    {
      title: 'الابتكار التربوي',
      description: 'نستخدم أحدث التقنيات والوسائل التعليمية لجعل عملية التعلم أكثر متعة وفعالية للتلميذ.',
      icon: FaLightbulb,
      color: '#ECC94B'
    }
  ];

  const features = [
    'التوافق التام مع البرنامج الرسمي لوزارة التربية',
    'أساتذة كفاءات ومن ذوي الخبرة الطويلة',
    'حصص مباشرة تفاعلية وشروحات مفصلة',
    'بنك ضخم من التمارين والامتحانات المصححة',
    'متابعة دورية لمستوى التلميذ وإعلام الأولياء'
  ];

  return (
    <Box dir="rtl" bg="gray.50">
      {/* Hero Section */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-50px"
          right="-50px"
          width="400px"
          height="400px"
          bg={primaryColor}
          opacity="0.05"
          borderRadius="full"
          filter="blur(100px)"
        />

        <Container maxW="1400px">
          <Grid templateColumns={{ base: '1fr', lg: '1.2fr 1fr' }} gap={12} alignItems="center">
            <GridItem>
              <VStack align="flex-start" spacing={8}>
                <Badge
                  bg={primaryColor}
                  color={secondaryColor}
                  px={6}
                  py={2}
                  borderRadius="full"
                  fontSize="md"
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="800"
                >
                  عن منصة كوبرا
                </Badge>
                <Heading
                  fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                  fontFamily="Tajawal, sans-serif"
                  fontWeight="900"
                  lineHeight="1.2"
                >
                  نحن هنا لنصنع <br />
                  <Text as="span" color={primaryColor}>جيل المتميزين</Text>
                </Heading>
                <Text fontSize="xl" fontFamily="Tajawal, sans-serif" color="whiteAlpha.900" lineHeight="1.8">
                  منصة كوبرا التعليمية هي المرجع الأول للتلاميذ في تونس، حيث نجمع بين الخبرة التربوية والابتكار الرقمي لتوفير أفضل تجربة تعلم ممكنة.
                </Text>
                <HStack spacing={4}>
                  <Button
                    as={RouterLink}
                    to="/packs"
                    bg={primaryColor}
                    color={secondaryColor}
                    size="lg"
                    px={10}
                    py={7}
                    fontWeight="800"
                    fontFamily="Tajawal, sans-serif"
                    _hover={{ bg: '#d4b016', transform: 'translateY(-2px)' }}
                    borderRadius="xl"
                  >
                    استكشف العروض
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/contact"
                    variant="outline"
                    borderColor="white"
                    color="white"
                    size="lg"
                    px={10}
                    py={7}
                    fontWeight="700"
                    fontFamily="Tajawal, sans-serif"
                    _hover={{ bg: 'whiteAlpha.100' }}
                    borderRadius="xl"
                  >
                    تواصل معنا
                  </Button>
                </HStack>
              </VStack>
            </GridItem>
            <GridItem>
              <MotionBox
                initial={{ opacity: 0, scale: 0.9, x: 20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box position="relative">
                  <Box
                    position="absolute"
                    inset="-20px"
                    bg={primaryColor}
                    opacity="0.1"
                    borderRadius="2xl"
                    transform="rotate(3deg)"
                  />
                  <Image
                    src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800"
                    alt="فريق تعليمي متميز"
                    borderRadius="2xl"
                    boxShadow="2xl"
                    fallbackSrc="https://ui-avatars.com/api/?name=Cobra+Team&background=F2C919&color=2C3E50&size=800"
                  />
                </Box>
              </MotionBox>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* Mission & Vision Section */}
      <Box py={{ base: 20, md: 32 }}>
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={16} alignItems="center">
            <VStack align="flex-start" spacing={10}>
              <Box>
                <HStack spacing={4} mb={6}>
                  <Icon as={FaBullseye} boxSize={10} color={primaryColor} />
                  <Heading size="xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                    رؤيتنا ومهمتنا
                  </Heading>
                </HStack>
                <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                  نهدف إلى تقليل الفوارق في الوصول إلى التعليم الجيد في تونس من خلال توفير منصة رقمية متكاملة تضع احتياجات التلميذ في صلب اهتماماتها. نحن نؤمن بأن كل تلميذ قادر على التميّز إذا توفرت له الأدوات الصحيحة.
                </Text>
              </Box>

              <VStack align="flex-start" spacing={4} w="100%">
                {features.map((feature, index) => (
                  <HStack key={index} spacing={4}>
                    <Icon as={FaCheckCircle} color={primaryColor} boxSize={5} />
                    <Text fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="700" color="gray.700">
                      {feature}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>

            <SimpleGrid columns={1} spacing={8}>
              <Box
                bg="white"
                p={10}
                borderRadius="2xl"
                boxShadow="xl"
                borderRight="8px solid"
                borderColor={primaryColor}
              >
                <VStack align="flex-start" spacing={6}>
                  <HStack spacing={4}>
                    <Icon as={FaUsers} boxSize={8} color={primaryColor} />
                    <Heading size="lg" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                      فريق من الخبراء
                    </Heading>
                  </HStack>
                  <Text fontSize="md" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                    يضم فريقنا نخبة من أفضل الأساتذة في تونس، ممن لديهم خبرة واسعة في برامج وزارة التربية، ملتزمون بمرافقة التلاميذ وتوجيههم نحو طريق النجاح.
                  </Text>
                </VStack>
              </Box>
              <Box
                bg="white"
                p={10}
                borderRadius="2xl"
                boxShadow="xl"
                borderRight="8px solid"
                borderColor={secondaryColor}
              >
                <VStack align="flex-start" spacing={6}>
                  <HStack spacing={4}>
                    <Icon as={FaChartLine} boxSize={8} color={secondaryColor} />
                    <Heading size="lg" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                      نتائج ملموسة
                    </Heading>
                  </HStack>
                  <Text fontSize="md" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                    تظهر إحصائياتنا أن التلاميذ المشتركين في منصة كوبرا يحققون تحسناً ملحوظاً في معدلاتهم، خاصة في المناظرات الوطنية والامتحانات الكبرى.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Values Section */}
      <Box py={{ base: 20, md: 32 }} bg="white">
        <Container maxW="1400px">
          <VStack spacing={4} mb={20} textAlign="center">
            <Heading
              fontSize={{ base: '3xl', md: '5xl' }}
              fontFamily="Tajawal, sans-serif"
              fontWeight="900"
              color={secondaryColor}
            >
              القيم التي تميزنا
            </Heading>
            <Text
              fontSize="lg"
              fontFamily="Tajawal, sans-serif"
              color="gray.600"
              maxW="700px"
            >
              نحن لا نقدم دروساً فقط، بل نبني بيئة تعليمية متكاملة تقوم على أسس متينة.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
            {values.map((value, index) => (
              <MotionBox
                key={index}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  h="100%"
                  bg="gray.50"
                  p={10}
                  borderRadius="2xl"
                  boxShadow="lg"
                  textAlign="center"
                  border="2px solid"
                  borderColor="gray.100"
                  _hover={{ borderColor: primaryColor, boxShadow: '2xl' }}
                >
                  <VStack spacing={6}>
                    <Box
                      p={5}
                      bg={`${value.color}15`}
                      borderRadius="2xl"
                      color={value.color}
                    >
                      <Icon as={value.icon} boxSize={12} />
                    </Box>
                    <Heading size="md" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                      {value.title}
                    </Heading>
                    <Text fontSize="md" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                      {value.description}
                    </Text>
                  </VStack>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Stats/Badge Section */}
      <Box py={20} bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`} color="white">
        <Container maxW="1400px">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} textAlign="center">
            <VStack>
              <Heading fontSize="5xl" color={primaryColor} fontWeight="900">20+</Heading>
              <Text fontSize="lg" fontWeight="700" fontFamily="Tajawal, sans-serif">أستاذ خبير</Text>
            </VStack>
            <VStack>
              <Heading fontSize="5xl" color={primaryColor} fontWeight="900">1200+</Heading>
              <Text fontSize="lg" fontWeight="700" fontFamily="Tajawal, sans-serif">تلميذ مسجل</Text>
            </VStack>
            <VStack>
              <Heading fontSize="5xl" color={primaryColor} fontWeight="900">1500+</Heading>
              <Text fontSize="lg" fontWeight="700" fontFamily="Tajawal, sans-serif">درس مسجل</Text>
            </VStack>
            <VStack>
              <Heading fontSize="5xl" color={primaryColor} fontWeight="900">95%</Heading>
              <Text fontSize="lg" fontWeight="700" fontFamily="Tajawal, sans-serif">نسبة رضا التلاميذ</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Final */}
      <Box py={{ base: 20, md: 32 }}>
        <Container maxW="1000px">
          <Box
            bg="white"
            p={{ base: 10, md: 20 }}
            borderRadius="3xl"
            boxShadow="2xl"
            textAlign="center"
            border="2px solid"
            borderColor={primaryColor}
            position="relative"
            overflow="hidden"
          >
            <VStack spacing={8}>
              <Heading size="2xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                انضم إلى عائلة المتميزين
              </Heading>
              <Text fontSize="xl" fontFamily="Tajawal, sans-serif" color="gray.600" maxW="700px">
                سجل تلميذك الآن في منصة كوبرا وابدأ رحلة النجاح تحت إشراف نخبة من أفضل الأساتذة في تونس.
              </Text>
              <Button
                as={RouterLink}
                to="/packs"
                bg={primaryColor}
                color={secondaryColor}
                size="xl"
                h="70px"
                px={16}
                fontSize="xl"
                fontWeight="900"
                fontFamily="Tajawal, sans-serif"
                _hover={{ bg: '#d4b016', transform: 'scale(1.05)' }}
                borderRadius="2xl"
                boxShadow="xl"
              >
                سجل الآن
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

const AboutPage = () => {
  return (
    <Box>
      <Header />
      <AboutContent />
      <Footer />
    </Box>
  );
};

export default AboutPage;

